import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ToggleOffOutlinedIcon from "@mui/icons-material/ToggleOffOutlined";
import ToggleOnOutlinedIcon from "@mui/icons-material/ToggleOnOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import React from "react";
import { BulkAction, ContentArchive } from "./useContentArchive";

const GREEN = "#2e7d32";
const AMBER = "#ed6c02";
const RED = "#d32f2f";

const useStyles = makeStyles((theme: any) => ({
  bar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    marginBottom: 10,
    borderRadius: 8,
    background: theme.palette.gradient,
    color: theme.palette.common.white,
  },
  count: { fontWeight: 600, flexGrow: 1, fontSize: 14 },
  action: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.secondary.light,
    textTransform: "none",
    fontWeight: 600,
    "&:hover": { backgroundColor: "#f0ecf5" },
  },
  clear: { color: theme.palette.common.white, textTransform: "none" },

  paper: { "& .MuiDialog-paper": { borderRadius: 12, overflow: "hidden" } },

  // Gradient header bar, matching the step headers used on the editor screens.
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 20px",
    background: theme.palette.gradient,
    color: theme.palette.common.white,
  },
  headerIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 38,
    height: 38,
    borderRadius: "50%",
    backgroundColor: "#ffffff26",
  },
  headerTitle: { fontSize: 16, fontWeight: 700, lineHeight: 1.2 },
  headerSubtitle: { fontSize: 12, color: "#EEEEEEB3" },

  content: { padding: "20px !important", minWidth: 440, maxWidth: 560 },

  // Confirm: the count is the thing the user is being asked to verify, so it leads.
  hero: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "14px 16px",
    borderRadius: 10,
    backgroundColor: "#F6F4F9",
    border: "1px solid #E3DEEA",
  },
  heroCount: {
    minWidth: 52,
    height: 52,
    padding: "0 10px",
    borderRadius: 10,
    background: theme.palette.gradient,
    color: theme.palette.common.white,
    fontSize: 22,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  heroLabel: {
    fontSize: 15,
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  heroHint: { fontSize: 12, color: "#6B6478", marginTop: 2 },

  note: {
    display: "flex",
    gap: 10,
    marginTop: 16,
    padding: "12px 14px",
    borderRadius: 8,
    backgroundColor: "#F3F6FB",
    borderLeft: "3px solid #5B7DB1",
  },
  noteIcon: { color: "#5B7DB1", fontSize: 20 },
  noteText: { fontSize: 12.5, color: "#4A5568", lineHeight: 1.55 },

  // Report: one tile per outcome so the totals read at a glance.
  tiles: { display: "flex", gap: 10 },
  tile: {
    flex: 1,
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid",
    textAlign: "center",
  },
  tileCount: { fontSize: 22, fontWeight: 700, lineHeight: 1.1 },
  tileLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },

  group: {
    marginTop: 14,
    padding: "12px 14px",
    borderRadius: 10,
    borderLeft: "3px solid",
    backgroundColor: "#FAFAFB",
  },
  groupHead: { display: "flex", alignItems: "center", gap: 8 },
  groupTitle: { fontSize: 13.5, fontWeight: 700 },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 10,
    maxHeight: 118,
    overflowY: "auto",
  },
  chip: {
    maxWidth: 220,
    backgroundColor: theme.palette.common.white,
    border: "1px solid #E0DCE6",
    fontSize: 12,
  },
  groupHint: { fontSize: 12, color: "#6B6478", marginTop: 8 },

  // Skipped items with the server's own reason, one per line.
  reasons: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginTop: 10,
    maxHeight: 180,
    overflowY: "auto",
  },
  reasonRow: {
    padding: "6px 10px",
    borderRadius: 6,
    backgroundColor: theme.palette.common.white,
    border: "1px solid #E0DCE6",
  },
  reasonName: { fontSize: 12.5, fontWeight: 600 },
  reasonText: { fontSize: 12, color: "#6B6478" },

  actions: { padding: "12px 20px 16px" },
  cancel: { textTransform: "none", color: "#6B6478" },
  confirm: {
    textTransform: "none",
    fontWeight: 600,
    minWidth: 108,
    background: theme.palette.gradient,
    color: theme.palette.common.white,
    "&:hover": { background: theme.palette.gradient, opacity: 0.92 },
    "&.Mui-disabled": { color: "#ffffff99" },
  },
}));

interface Props {
  archive: ContentArchive;
  /** Current page rows, used to turn ids back into names for the report. */
  rows: any[];
  /** Field holding the human-readable name of a row. */
  nameKey?: string;
  getRowId?: (row: any) => string;
}

type Label = { singular: string; plural: string };

/**
 * Names of every row seen so far, by id, so the report can name items that are no longer on screen:
 * ones picked on another page, and ones the action itself removed from the list. It lives outside
 * the component because ContentShell unmounts its children while the list reloads after an action.
 * Ids are Mongo ids, unique across content types, so one cache serves every screen.
 */
const knownNames = new Map<string, string>();

const defaultGetRowId = (row: any) => row?._id;

/** Everything the bar and its dialogs say about one action. */
const ACTION_COPY: Record<
  BulkAction,
  {
    verb: string;
    verbing: string;
    done: string;
    Icon: React.ElementType;
    heroHint: string;
    note: (label: Label) => string;
    skippedTitle: string;
    /** Shown under skipped items when the server gives no reason per item. */
    skippedHint?: (label: Label) => string;
    failedHint: (label: Label) => string;
  }
> = {
  archive: {
    verb: "Archive",
    verbing: "Archiving",
    done: "archived",
    Icon: ArchiveOutlinedIcon,
    heroHint: "They are hidden from this list and every picker in the CMS.",
    note: (label) =>
      `Nothing is deleted: everything already using these ${label.plural} keeps working, and you can restore them at any time from the archive.`,
    skippedTitle: "Still live",
    skippedHint: (label) =>
      `Deactivate these ${label.plural} first, then archive them.`,
    failedHint: (label) =>
      `These ${label.plural} were already archived or no longer exist.`,
  },
  restore: {
    verb: "Restore",
    verbing: "Restoring",
    done: "restored",
    Icon: UnarchiveOutlinedIcon,
    heroHint: "They return to the main list, still inactive.",
    note: (label) =>
      `Restored ${label.plural} come back in the inactive state they were archived in, so nothing goes live until you activate it.`,
    skippedTitle: "Skipped",
    failedHint: (label) =>
      `These ${label.plural} were already restored or no longer exist.`,
  },
  enable: {
    verb: "Enable",
    verbing: "Enabling",
    done: "enabled",
    Icon: ToggleOnOutlinedIcon,
    heroHint: "They go live for players straight away.",
    note: (label) =>
      `Any ${label.singular} that cannot be enabled right now, for example one outside its schedule, is skipped and listed in the report with the reason.`,
    skippedTitle: "Skipped",
    failedHint: (label) =>
      `These ${label.plural} were already enabled, were changed by someone else, or no longer exist.`,
  },
  disable: {
    verb: "Disable",
    verbing: "Disabling",
    done: "disabled",
    Icon: ToggleOffOutlinedIcon,
    heroHint: "They stop showing to players straight away.",
    note: (label) =>
      `A scheduled ${label.singular} disabled here stays off: its schedule will not switch it back on until you enable it again.`,
    skippedTitle: "Skipped",
    failedHint: (label) =>
      `These ${label.plural} were already disabled, were changed by someone else, or no longer exist.`,
  },
  delete: {
    verb: "Delete",
    verbing: "Deleting",
    done: "deleted",
    Icon: DeleteOutlineIcon,
    heroHint: "This cannot be undone.",
    note: (label) =>
      `Any ${label.singular} that is still enabled or still in use is skipped and listed in the report with the reason.`,
    skippedTitle: "Skipped",
    failedHint: (label) => `These ${label.plural} no longer exist.`,
  },
};

/** One outcome group in the report: coloured accent, count, and the affected items by name. */
const ResultGroup = ({
  color,
  tint,
  icon,
  title,
  ids,
  nameOf,
  hint,
  reasons,
}: {
  color: string;
  tint: string;
  icon: React.ReactNode;
  title: string;
  ids: string[];
  nameOf: (id: string) => string;
  hint?: string;
  /** The server's reason per id; when given, each item is listed with its own reason. */
  reasons?: Map<string, string>;
}) => {
  const styles = useStyles();
  if (!ids?.length) return null;

  return (
    <Box
      className={styles.group}
      style={{ borderLeftColor: color, backgroundColor: tint }}
    >
      <Box className={styles.groupHead}>
        <Box style={{ color, display: "flex" }}>{icon}</Box>
        <Typography className={styles.groupTitle} style={{ color }}>
          {title} ({ids.length})
        </Typography>
      </Box>
      {reasons ? (
        <Box className={styles.reasons}>
          {ids.map((id) => (
            <Box key={id} className={styles.reasonRow}>
              <Typography className={styles.reasonName}>{nameOf(id)}</Typography>
              <Typography className={styles.reasonText}>
                {reasons.get(id)}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Box className={styles.chips}>
          {ids.map((id) => (
            <Chip
              key={id}
              size="small"
              className={styles.chip}
              label={nameOf(id)}
            />
          ))}
        </Box>
      )}
      {hint && <Typography className={styles.groupHint}>{hint}</Typography>}
    </Box>
  );
};

/**
 * The selection bar plus the confirm and report dialogs for one list screen. Rendered directly
 * above the table; it shows nothing until rows are selected, so the screen looks unchanged
 * until the user starts a bulk action.
 */
const ArchiveActionBar = ({
  archive,
  rows,
  nameKey = "title",
  getRowId = defaultGetRowId,
}: Props) => {
  const styles = useStyles();
  const { selectedIds, label, mode, pending, result } = archive;

  // The dialogs keep showing their last content while they fade out after being closed.
  const lastPending = React.useRef(pending);
  if (pending) lastPending.current = pending;
  const lastResult = React.useRef(result);
  if (result) lastResult.current = result;

  const confirmCopy = ACTION_COPY[lastPending.current?.action ?? "archive"];
  const confirmCount = lastPending.current?.ids.length ?? 0;
  const confirmNoun = confirmCount === 1 ? label.singular : label.plural;
  const ConfirmIcon = confirmCopy.Icon;

  const report = lastResult.current;
  const reportCopy = ACTION_COPY[report?.action ?? "archive"];

  React.useEffect(() => {
    (rows ?? []).forEach((row) => {
      const name = row?.[nameKey];
      if (name) knownNames.set(getRowId(row), name);
    });
  }, [rows, nameKey, getRowId]);

  const nameOf = (id: string) => knownNames.get(id) || id;

  const succeeded = report?.successIds?.length ?? 0;
  const skipped = report?.skippedIds?.length ?? 0;
  const failed = report?.failedIds?.length ?? 0;
  const unsent = report?.unsentIds?.length ?? 0;
  const total = succeeded + skipped + failed + unsent;

  const skipReasons = React.useMemo(
    () =>
      report?.skipReasons?.length
        ? new Map(report.skipReasons.map(({ id, reason }) => [id, reason]))
        : undefined,
    [report],
  );

  return (
    <>
      {selectedIds.length > 0 && (
        <Box className={styles.bar}>
          <Typography className={styles.count}>
            {selectedIds.length} {mode ? `${mode} ` : ""}
            {selectedIds.length === 1 ? label.singular : label.plural} selected
          </Typography>
          <Button className={styles.clear} onClick={archive.clearSelection}>
            Clear
          </Button>
          {archive.actions.map((action) => {
            const { verb, Icon } = ACTION_COPY[action];
            return (
              <Button
                key={action}
                variant="contained"
                className={styles.action}
                startIcon={<Icon />}
                onClick={() => archive.requestAction(undefined, action)}
              >
                {verb}
              </Button>
            );
          })}
        </Box>
      )}

      <Dialog
        open={archive.confirmOpen}
        onClose={archive.closeConfirm}
        className={styles.paper}
      >
        <Box className={styles.header}>
          <Box className={styles.headerIcon}>
            <ConfirmIcon />
          </Box>
          <Box>
            <Typography className={styles.headerTitle}>
              {confirmCopy.verb} content
            </Typography>
            <Typography className={styles.headerSubtitle}>
              Please confirm this action
            </Typography>
          </Box>
        </Box>

        <DialogContent className={styles.content}>
          <Box className={styles.hero}>
            <Box className={styles.heroCount}>{confirmCount}</Box>
            <Box>
              <Typography className={styles.heroLabel}>
                {confirmNoun} will be {confirmCopy.done}
              </Typography>
              <Typography className={styles.heroHint}>
                {confirmCopy.heroHint}
              </Typography>
            </Box>
          </Box>

          <Box className={styles.note}>
            <InfoOutlinedIcon className={styles.noteIcon} />
            <Typography className={styles.noteText}>
              {confirmCopy.note(label)}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions className={styles.actions}>
          <Button
            onClick={archive.closeConfirm}
            className={styles.cancel}
            disabled={archive.busy}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className={styles.confirm}
            onClick={archive.confirmAction}
            disabled={archive.busy}
            autoFocus
            startIcon={
              archive.busy ? (
                <CircularProgress size={15} color="inherit" />
              ) : undefined
            }
          >
            {archive.busy ? `${confirmCopy.verbing}…` : confirmCopy.verb}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!result}
        onClose={archive.closeResult}
        className={styles.paper}
      >
        <Box className={styles.header}>
          <Box className={styles.headerIcon}>
            {succeeded === total ? (
              <CheckCircleOutlineIcon />
            ) : (
              <InfoOutlinedIcon />
            )}
          </Box>
          <Box>
            <Typography className={styles.headerTitle}>
              {reportCopy.verb} report
            </Typography>
            <Typography className={styles.headerSubtitle}>
              {succeeded} of {total}{" "}
              {total === 1 ? label.singular : label.plural} {reportCopy.done}
            </Typography>
          </Box>
        </Box>

        <DialogContent className={styles.content}>
          <Box className={styles.tiles}>
            <Box
              className={styles.tile}
              style={{ borderColor: "#C8E6C9", backgroundColor: "#F1F8F2" }}
            >
              <Typography className={styles.tileCount} style={{ color: GREEN }}>
                {succeeded}
              </Typography>
              <Typography className={styles.tileLabel} style={{ color: GREEN }}>
                {reportCopy.done}
              </Typography>
            </Box>
            <Box
              className={styles.tile}
              style={{ borderColor: "#FFE0B2", backgroundColor: "#FFF8F0" }}
            >
              <Typography className={styles.tileCount} style={{ color: AMBER }}>
                {skipped}
              </Typography>
              <Typography className={styles.tileLabel} style={{ color: AMBER }}>
                Skipped
              </Typography>
            </Box>
            <Box
              className={styles.tile}
              style={{ borderColor: "#FFCDD2", backgroundColor: "#FEF4F4" }}
            >
              <Typography className={styles.tileCount} style={{ color: RED }}>
                {failed + unsent}
              </Typography>
              <Typography className={styles.tileLabel} style={{ color: RED }}>
                Not processed
              </Typography>
            </Box>
          </Box>

          <ResultGroup
            color={GREEN}
            tint="#F1F8F2"
            icon={<CheckCircleOutlineIcon fontSize="small" />}
            title={
              reportCopy.done.charAt(0).toUpperCase() + reportCopy.done.slice(1)
            }
            ids={report?.successIds ?? []}
            nameOf={nameOf}
          />

          <ResultGroup
            color={AMBER}
            tint="#FFF8F0"
            icon={<RemoveCircleOutlineIcon fontSize="small" />}
            title={reportCopy.skippedTitle}
            ids={report?.skippedIds ?? []}
            nameOf={nameOf}
            reasons={skipReasons}
            hint={skipReasons ? undefined : reportCopy.skippedHint?.(label)}
          />

          <ResultGroup
            color={RED}
            tint="#FEF4F4"
            icon={<ErrorOutlineIcon fontSize="small" />}
            title="Not processed"
            ids={report?.failedIds ?? []}
            nameOf={nameOf}
            hint={reportCopy.failedHint(label)}
          />

          <ResultGroup
            color={RED}
            tint="#FEF4F4"
            icon={<ErrorOutlineIcon fontSize="small" />}
            title="Request failed"
            ids={report?.unsentIds ?? []}
            nameOf={nameOf}
            hint={`These ${label.plural} were not processed because a request failed${
              report?.unsentReason
                ? `: ${report.unsentReason.replace(/\.$/, "")}`
                : ""
            }. They are still selected, so you can try again.`}
          />
        </DialogContent>

        <DialogActions className={styles.actions}>
          <Button
            variant="contained"
            className={styles.confirm}
            onClick={archive.closeResult}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ArchiveActionBar;
